<template>
    <v-card variant="outlined" class="activity-alert-card mb-2"
            :class="cardClasses"
            :style="{ borderLeft: `4px solid ${cardColor}`, cursor: 'pointer' }"
            @click="openDetails">
        <v-card-text class="pa-3">
            <div class="d-flex align-center ga-2">
                <v-avatar :color="cardColor" size="36" class="flex-shrink-0" :variant="showUnread ? 'flat' : 'tonal'">
                    <v-icon :icon="cardIcon" size="20" :color="showUnread ? 'white' : cardColor"/>
                </v-avatar>

                <div class="flex-grow-1 min-w-0">
                    <div class="d-flex align-center ga-1" :class="{ 'flex-wrap': smAndUp }">
                        <span class="text-body-2 font-weight-bold"
                              :class="smAndUp ? 'text-truncate' : 'headline-clamp'">
                            {{ headline || notification.title || alertLabel(notification) }}
                        </span>
                        <AlertSeverityChip :alert="notification"/>
                        <v-chip v-if="isNew" size="x-small" color="primary" variant="tonal"
                                class="font-weight-bold flex-shrink-0">New</v-chip>
                    </div>
                    <div class="d-flex align-center ga-2 flex-wrap">
                        <span class="text-caption text-medium-emphasis text-truncate">{{ headline ? alertLabel(notification) : notification.asset }}</span>
                        <span v-if="formattedTime" class="text-caption text-medium-emphasis flex-shrink-0">
                            <template v-if="formatAgo">{{ formatAgo }} · </template>{{ formattedTime }}
                        </span>
                    </div>
                    <div v-if="secondLine" class="text-caption text-medium-emphasis text-truncate">
                        <v-icon :icon="speedDetail ? 'mdi-speedometer' : 'mdi-map-marker'" size="11" class="me-1"/>{{ secondLine }}
                    </div>
                </div>

                <v-icon v-if="showUnread" icon="mdi-circle" size="10" color="error" class="flex-shrink-0"
                        aria-label="Unread"/>

                <div v-if="smAndUp" class="d-flex ga-1 flex-shrink-0">
                    <v-tooltip v-if="alertCoords" location="top" text="Open in Google Maps">
                        <template #activator="{ props: tp }">
                            <v-btn v-bind="tp" icon="mdi-google-maps" aria-label="Open in Google Maps"
                                   size="x-small" variant="text" color="medium-emphasis"
                                   @click.stop="openGoogleMaps"/>
                        </template>
                    </v-tooltip>
                    <v-tooltip v-if="isOwnNotification" location="top" :text="readToggleText">
                        <template #activator="{ props: tp }">
                            <v-btn v-bind="tp" :aria-label="readToggleText"
                                   :icon="isRead ? 'mdi-email-outline' : 'mdi-email-open-outline'"
                                   size="x-small" variant="text" color="medium-emphasis"
                                   @click.stop="$emit('toggle-read', notification)"/>
                        </template>
                    </v-tooltip>
                    <v-tooltip location="top" text="View details">
                        <template #activator="{ props: tp }">
                            <v-btn v-bind="tp" icon="mdi-information-outline" aria-label="View details"
                                   size="x-small" variant="text" color="medium-emphasis"
                                   @click.stop="openDetails"/>
                        </template>
                    </v-tooltip>
                    <v-tooltip v-if="showDelivery" location="top" text="Delivery details">
                        <template #activator="{ props: tp }">
                            <v-btn v-bind="tp" icon="mdi-send-check-outline" aria-label="Delivery details"
                                   size="x-small" variant="text" color="medium-emphasis"
                                   @click.stop="$emit('delivery', notification)"/>
                        </template>
                    </v-tooltip>
                </div>
                <v-menu v-else location="bottom end">
                    <template #activator="{ props: mp }">
                        <v-btn v-bind="mp" icon="mdi-dots-vertical" aria-label="Alert actions"
                               size="small" variant="text" color="medium-emphasis" class="flex-shrink-0"
                               @click.stop/>
                    </template>
                    <v-list density="compact">
                        <v-list-item v-if="alertCoords" prepend-icon="mdi-google-maps" title="Open in Google Maps"
                                     @click="openGoogleMaps"/>
                        <v-list-item v-if="isOwnNotification" :title="readToggleText"
                                     :prepend-icon="isRead ? 'mdi-email-outline' : 'mdi-email-open-outline'"
                                     @click="$emit('toggle-read', notification)"/>
                        <v-list-item prepend-icon="mdi-information-outline" title="View details"
                                     @click="openDetails"/>
                        <v-list-item v-if="showDelivery" prepend-icon="mdi-send-check-outline" title="Delivery details"
                                     @click="$emit('delivery', notification)"/>
                    </v-list>
                </v-menu>
            </div>
        </v-card-text>

        <v-dialog v-model="showDetails" max-width="600" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center pa-3">
                    <span class="text-body-1 font-weight-bold">Alert Details</span>
                    <v-spacer/>
                    <v-btn icon="mdi-close" aria-label="Close" variant="text" size="small" @click="showDetails = false"/>
                </v-card-title>
                <v-divider/>
                <v-card-text class="pa-0">
                    <AlertPayloadDetails :notification="notification"/>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script setup>
import {ALERT_TYPE, alertColor, alertDetail, alertHeadline, alertIcon, alertLabel, alertSeverity, payloadLatLng, rowTypeOf} from '@/composables/alertTypes';
import {ref, computed} from 'vue';
import {useDisplay} from 'vuetify';
import {formatCompanyDateTime, parseApiDate} from "@/composables/useTimezone";
import {useAuthStore} from "@/stores/authStore";
import AlertPayloadDetails from "@/components/cards/AlertPayloadDetails.vue";
import AlertSeverityChip from "@/components/cards/AlertSeverityChip.vue";

const props = defineProps({
    notification: {type: Object, required: true},
    nowTick: {type: Number, default: () => Date.now()},
    address: {type: String, default: null},
    // Unread for this viewer when the page opened, before it was auto marked read
    isNew: {type: Boolean, default: false},
    // Admin pages: offer the delivery channels dialog
    showDelivery: {type: Boolean, default: false},
});
const emit = defineEmits(['toggle-read', 'delivery']);

const authStore = useAuthStore();
const {smAndUp} = useDisplay();
const showDetails = ref(false);

// Opening the details counts as reading it. The endpoint toggles, so only fire
// on a row that is both ours and still unread.
const openDetails = () => {
    showDetails.value = true;
    if (isOwnNotification.value && !isRead.value) {
        emit('toggle-read', props.notification);
    }
};

const headline = computed(() => alertHeadline(props.notification));
const cardColor = computed(() => alertColor(props.notification));
const cardIcon = computed(() => alertIcon(props.notification));

// The read flag is per notification row, and the company feed carries other
// users' rows, which toggleRead refuses.
const isOwnNotification = computed(() => {
    const owner = props.notification?.user_id;
    return owner != null && String(owner) === String(authStore.user?.id);
});

const isRead = computed(() => {
    const n = props.notification;
    return n.read === true || n.read === 1 || n.is_read === true || n.is_read === 1;
});

// A colleague's read flag says nothing about this viewer, so only own rows show as unread.
const showUnread = computed(() => isOwnNotification.value && !isRead.value);
const readToggleText = computed(() => (isRead.value ? 'Mark unread' : 'Mark read'));

const cardClasses = computed(() => {
    const severity = alertSeverity(props.notification);
    return {
        'unread-card': showUnread.value,
        'new-card': props.isNew,
        'sev-critical': severity === 'critical',
        'sev-high': severity === 'high',
    };
});

const formattedTime = computed(() => {
    const ts = props.notification?.event_timestamp;
    if (!ts) return null;
    return formatCompanyDateTime(ts);
});

const formatAgo = computed(() => {
    const ms = parseApiDate(props.notification?.event_timestamp)?.getTime();
    if (!ms) return '';
    const m = Math.floor((props.nowTick - ms) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
});

// How far over the limit, or the payload address, else the geocoded address.
const detail = computed(() => alertDetail(props.notification));
const secondLine = computed(() => detail.value || props.address);
const speedDetail = computed(() => !!detail.value && rowTypeOf(props.notification) === ALERT_TYPE.SPEED
    && detail.value.endsWith('over the limit'));

const alertCoords = computed(() => payloadLatLng(props.notification?.payload));

const openGoogleMaps = () => {
    if (!alertCoords.value) return;
    window.open(`https://www.google.com/maps?q=${alertCoords.value.lat},${alertCoords.value.lng}`, '_blank', 'noopener');
};
</script>

<style scoped>
.min-w-0 {
    min-width: 0;
}
.headline-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-width: 0;
}
.activity-alert-card {
    border-radius: 8px;
    transition: background-color 0.15s;
}
.activity-alert-card:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.03);
}
.unread-card,
.new-card {
    background-color: rgba(var(--v-theme-primary), 0.05);
}
.sev-high {
    background-color: rgba(var(--v-theme-warning), 0.06);
}
.sev-critical {
    background-color: rgba(var(--v-theme-error), 0.06);
}
</style>
